import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

const formatOvertimeUndertimeDisplay = (value) => {
  if (!value || value === 0 || value === "0" || value === "-") {
    return "-";
  }

  if (typeof value === "string" && value.includes("m")) {
    return value;
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "-";
  if (numValue === 0) return "-";

  return `${numValue.toFixed(1)}m`;
};

const useAttendance = ({ BASE_URL, language, profile, viewType, getEmployeeName, loadAllCompanyConfigs }) => {
  const [attendanceData, setAttendanceData] = useState([]);
  // Starts true (not false) so the page-level skeleton is guaranteed to be
  // visible on first paint instead of racing the mount effect that flips it.
  const [loading, setLoading] = useState(true);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Mirror of attendanceData for synchronous merges (append/export flows)
  const attendanceDataRef = useRef([]);
  useEffect(() => {
    attendanceDataRef.current = attendanceData;
  }, [attendanceData]);

  const loadAttendance = async (
    date,
    employeesList,
    page = 1,
    append = false,
    forceRefresh = false,
    employeeIds = null,
    sync = false
  ) => {
    try {
      setLoading(true);
      const storedViewType =
        viewType ||
        localStorage.getItem('managerViewType') ||
        localStorage.getItem('viewType') ||
        'admin';

      // The attendance page is READ-ONLY by default (sync=false → no-payroll-sync
      // endpoint). Browsing, pagination, filtering, search and export never sync
      // payroll — otherwise filtering "absent" (which matches almost everyone)
      // would sync the whole roster. Payroll is kept fresh at write time (device
      // punch / manual edit), on login (own record), and by the nightly cron.
      // Only the explicit Refresh button passes sync=true.
      const endpoint = sync ? 'attendance/visibility' : 'attendance/visibility-no-payroll-sync';
      console.log(`👁️ Loading attendance via ${endpoint} (viewType: ${storedViewType}, date: ${date}, forceRefresh: ${forceRefresh}, sync: ${sync})`);
      try {
        const url = new URL(`${BASE_URL}/${endpoint}`);
        url.searchParams.append('date', date);
        if (forceRefresh) {
          url.searchParams.append('forceRefresh', 'true');
        }
        // Chunked loading: when a subset is requested, the backend only
        // computes these employees instead of the whole roster.
        if (Array.isArray(employeeIds) && employeeIds.length > 0) {
          url.searchParams.append('employeeIds', employeeIds.map(String).join(','));
        }

        const resp = await apiFetch(url.toString(), {
          method: 'GET',
          headers: {
            "Accept-Language": language,
            "x-view-type": storedViewType,
          },
        });

        if (!resp.ok) {
          console.warn(`Attendance visibility fetch failed: ${resp.status}`);
          // A failed append must not wipe the rows already on screen
          if (!append) {
            attendanceDataRef.current = [];
            setAttendanceData([]);
          }
          return attendanceDataRef.current;
        }

        const json = await resp.json();
        const attendanceRecords = json?.data?.attendance || [];
        console.log(`✅ Loaded ${attendanceRecords.length} attendance records for ${date}`);

        const rows = attendanceRecords
          .map((record) => {
            const employeeId =
              record.employeeId ||
              record.employee?.infinityCode ||
              record.employee?.id ||
              record.employee?.employeeId ||
              "";

            const fallbackNameFromRecord =
              language === "ar"
                ? record.employee?.arabicName || record.employee?.name_ar || record.employee?.sr || record.employee?.name
                : record.employee?.name || record.employee?.sr || record.employee?.fullName || record.employee?.englishName;

            const employeeName =
              record.employeeName ||
              fallbackNameFromRecord ||
              getEmployeeName(employeeId, attendanceRecords, language) ||
              String(employeeId || record.employee?.infinityCode || record.employee?.id || record.employee?.employeeId || "");

            return {
              id: record.id || `${employeeId}-${date}`,
              employeeId,
              employeeName,
              clientName: record.clientName || profile?.clientName || localStorage.getItem('clientName') || "",
              workingCompany: record.workingCompany || null,
              employeeType: record.employeeType || null,
              date: date,
              checkIn: record.checkIn ? new Date(record.checkIn).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '-',
              checkOut: record.checkOut ? new Date(record.checkOut).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '-',
              hoursWorked: record.workingHours ?? record.hoursWorked ?? '-',
              status: record.status ?? '-',
              notes: record.notes ?? '-',
              overTime: formatOvertimeUndertimeDisplay(record.overTime),
              underTime: formatOvertimeUndertimeDisplay(record.underTime),
              manuallyEdited: record.manuallyEdited ?? false,
              editedByIn: record.editedByIn ?? null,
              editedByOut: record.editedByOut ?? null,
            };
          });

        rows.sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));

        let finalRows = rows;
        if (append) {
          // Merge: rows for the same employee replace the old ones, new rows
          // are added, and the table stays sorted by name.
          const incoming = new Set(rows.map((r) => String(r.employeeId)));
          finalRows = [
            ...attendanceDataRef.current.filter((r) => !incoming.has(String(r.employeeId))),
            ...rows,
          ];
          finalRows.sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));
          attendanceDataRef.current = finalRows;
          setAttendanceData(finalRows);
        } else {
          attendanceDataRef.current = finalRows;
          setAttendanceData(finalRows);
          setCurrentPage(1);
        }

        // Non-blocking: the table renders immediately; company configs (used by
        // the overtime approval indicators) arrive moments later.
        loadAllCompanyConfigs(rows).catch((err) =>
          console.warn('loadAllCompanyConfigs failed:', err)
        );

        console.log(`✅ Loaded ${rows.length} attendance records for ${date}`);
        return finalRows;
      } catch (err) {
        console.error(`Error fetching attendance by visibility:`, err);
        // A failed append must not wipe the rows already on screen
        if (!append) {
          attendanceDataRef.current = [];
          setAttendanceData([]);
        }
        return attendanceDataRef.current;
      }
    } catch (error) {
      console.error('Error loading attendance:', error);
      return attendanceDataRef.current;
    } finally {
      setLoading(false);
    }
  };

  // Refresh a SINGLE employee's row after a check-in/out reset or revert.
  // Uses the backend's employeeIds filter so only that employee is recomputed
  // instead of force-refreshing the whole page (which recalculated everyone).
  const refreshEmployeeRow = async (employeeId, date) => {
    const storedViewType =
      viewType ||
      localStorage.getItem('managerViewType') ||
      localStorage.getItem('viewType') ||
      'admin';

    const url = new URL(`${BASE_URL}/attendance/visibility`);
    url.searchParams.append('date', date);
    url.searchParams.append('forceRefresh', 'true');
    url.searchParams.append('employeeIds', String(employeeId));

    const resp = await apiFetch(url.toString(), {
      method: 'GET',
      headers: {
        "Accept-Language": language,
        "x-view-type": storedViewType,
      },
    });

    if (!resp.ok) {
      throw new Error(`Single-employee refresh failed: ${resp.status}`);
    }

    const json = await resp.json();
    const record = (json?.data?.attendance || [])[0];
    if (!record) {
      throw new Error('Single-employee refresh returned no record');
    }

    setAttendanceData((prev) =>
      prev.map((row) =>
        String(row.employeeId) === String(employeeId)
          ? {
            ...row,
            id: record.id || row.id,
            checkIn: record.checkIn ? new Date(record.checkIn).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '-',
            checkOut: record.checkOut ? new Date(record.checkOut).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '-',
            hoursWorked: record.workingHours ?? '-',
            status: record.status ?? '-',
            notes: record.notes ?? '-',
            overTime: formatOvertimeUndertimeDisplay(record.overTime),
            underTime: formatOvertimeUndertimeDisplay(record.underTime),
            manuallyEdited: record.manuallyEdited ?? false,
            editedByIn: record.editedByIn ?? null,
            editedByOut: record.editedByOut ?? null,
          }
          : row
      )
    );
  };

  const normalizeServerRecord = (raw) => ({
    ...raw,
    date: raw.date
      ? new Date(raw.date).toISOString().split('T')[0]
      : raw.date,
    checkIn: raw.checkIn
      ? new Date(raw.checkIn).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
      : '-',
    checkOut: raw.checkOut
      ? new Date(raw.checkOut).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
      : '-',
    manuallyEdited: raw.manuallyEdited ?? false,
    editedByIn: raw.editedByIn ?? null,
    editedByOut: raw.editedByOut ?? null,
  });

  return {
    attendanceData,
    attendanceDataRef,
    loading,
    isRecalculating,
    setIsRecalculating,
    currentPage,
    setCurrentPage,
    loadAttendance,
    refreshEmployeeRow,
    normalizeServerRecord,
    formatOvertimeUndertimeDisplay,
  };
};

export default useAttendance;